import type { LiveTurnState } from '@erp/rpc';
import { z } from 'zod';
import { persistClaim, releaseClaim } from './liveTurn';
import type {
  FrontendToolDefinition,
  FrontendToolResult,
} from '@/hooks/useFrontendTool';

/**
 * Awaitable frontend tools run in the browser while the backend blocks the
 * agent on their result. The `chat` stream runs them on `tool-call`; a tab that
 * reattached after a refresh sees the same calls only in turn snapshots, so
 * both paths share this module and one `claimed` set to run each call once.
 */

type LiveToolCall = LiveTurnState['toolCalls'][number];

export interface PendingFrontendToolCall {
  call: LiveToolCall;
  tool: FrontendToolDefinition;
}

/** Tools the agent is offered: `enabled: false` ones are mounted but unavailable. */
export function toolsByName(
  tools: readonly FrontendToolDefinition[],
): Map<string, FrontendToolDefinition> {
  const byName = new Map<string, FrontendToolDefinition>();
  for (const t of tools) if (t.enabled !== false) byName.set(t.name, t);
  return byName;
}

/**
 * Marks a call as started. A write tool's claim also survives a reload of this
 * tab: its side effect may have committed before the result POST landed, and
 * running it again would duplicate it (a second run, a second task). The cost
 * is that such a call is never retried, so the agent's bridge times out
 * instead. Another tab (a duplicated tab copies this tab's storage) is kept
 * from running the same call by `runAwaitableTool`'s cross-tab lock.
 */
export function markClaimed(
  claimed: Set<string>,
  tool: FrontendToolDefinition,
  toolCallId: string,
): void {
  claimed.add(toolCallId);
  if (tool.schema.readOnlyHint !== true) persistClaim(toolCallId);
}

/**
 * Takes the running calls of a snapshot that a registered awaitable tool
 * answers and nobody has run yet, marking them claimed. A call whose tool is
 * not registered yet, or that `canRun` rejects, stays unclaimed so a later
 * registration can pick it up.
 */
export function claimPendingAwaitableCalls(
  toolCalls: readonly LiveToolCall[],
  tools: ReadonlyMap<string, FrontendToolDefinition>,
  claimed: Set<string>,
  canRun: (tool: FrontendToolDefinition) => boolean = () => true,
): PendingFrontendToolCall[] {
  const pending: PendingFrontendToolCall[] = [];
  for (const call of toolCalls) {
    if (call.status !== 'running' || claimed.has(call.toolCallId)) continue;
    const tool = tools.get(call.toolName);
    if (!tool?.awaitable || !canRun(tool)) continue;
    markClaimed(claimed, tool, call.toolCallId);
    pending.push({ call, tool });
  }
  return pending;
}

export interface RunAwaitableToolDeps {
  submit: (input: {
    toolCallId: string;
    result: FrontendToolResult;
  }) => Promise<unknown>;
  onFailed: (toolCallId: string, result: FrontendToolResult) => void;
}

/**
 * Runs the tool's handler and POSTs its result so the agent continues in the
 * same turn. `execute` never throws (failures arrive as `{ ok: false }`), so
 * the agent always gets a well-formed result rather than a bridge timeout.
 *
 * When another tab holds the call's lock a write call is left to that tab and
 * keeps its claim. A read-only call queues for the lock instead, keeping its
 * claim so this tab does not queue twice: the agent is blocked on the read, so
 * no new snapshot may arrive to retry it. Once granted, it is skipped if the
 * holder completed it, and run otherwise. The wait is bounded under the
 * bridge's own timeout; if it runs out the claim is dropped. `signal` is the
 * live turn's: once aborted (Stop, reset, a newer session) a queued call
 * stops waiting and a granted one does not run its handler.
 */
export async function runAwaitableTool(
  tool: FrontendToolDefinition,
  toolCallId: string,
  args: Record<string, unknown>,
  deps: RunAwaitableToolDeps,
  claimed: Set<string>,
  signal?: AbortSignal,
): Promise<void> {
  const queue = tool.schema.readOnlyHint === true;
  const outcome = await runOnceAcrossTabs(
    toolCallId,
    () => executeAndSubmit(tool, toolCallId, args, deps),
    queue,
    signal,
  );
  // Nothing ran: free the claim (in memory and persisted) so a reattach
  // snapshot can run the call instead of the agent waiting out its bridge.
  if (outcome === 'cancelled') {
    claimed.delete(toolCallId);
    releaseClaim(toolCallId);
  } else if (outcome === 'contended' && queue) claimed.delete(toolCallId);
}

/**
 * What became of a call: `handled` (this tab ran it or another completed it),
 * `contended` (another tab holds the lock, or a queued wait timed out) or
 * `cancelled` (its turn ended before the handler started).
 */
type RunOutcome = 'handled' | 'contended' | 'cancelled';

/** How long a read-only call waits for another tab's lock; under the 30s bridge timeout. */
const LOCK_WAIT_MS = 25_000;

const SUBMIT_RETRY_DELAYS_MS = [500, 1500, 4000];

/**
 * Runs the handler once, then POSTs the result, retrying a failed POST with
 * backoff against the in-memory result (the handler is never re-run). Resolves
 * true only once the POST landed.
 */
async function executeAndSubmit(
  tool: FrontendToolDefinition,
  toolCallId: string,
  args: Record<string, unknown>,
  { submit, onFailed }: RunAwaitableToolDeps,
): Promise<boolean> {
  const result = await tool.execute(args as never);
  if (!result.ok) {
    console.error(`awaitable frontend tool ${tool.name} failed`, result.error);
    onFailed(toolCallId, result);
  }
  for (let attempt = 0; ; attempt++) {
    try {
      await submit({ toolCallId, result });
      return true;
    } catch (postErr) {
      console.error('failed to submit frontend tool result', postErr);
      const delay = SUBMIT_RETRY_DELAYS_MS[attempt];
      if (delay === undefined) return false;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}

const COMPLETED_KEY = 'nami.completedToolCalls.v1';
const COMPLETED_TTL_MS = 60 * 60 * 1000;

const completedSchema = z.record(z.string(), z.number());

function readCompleted(now: number): Record<string, number> {
  try {
    const parsed = completedSchema.safeParse(
      JSON.parse(localStorage.getItem(COMPLETED_KEY) ?? '{}'),
    );
    if (!parsed.success) return {};
    return Object.fromEntries(
      Object.entries(parsed.data).filter(
        ([, at]) => now - at < COMPLETED_TTL_MS,
      ),
    );
  } catch {
    return {};
  }
}

function recordCompleted(toolCallId: string, now: number): void {
  try {
    localStorage.setItem(
      COMPLETED_KEY,
      JSON.stringify({ ...readCompleted(now), [toolCallId]: now }),
    );
  } catch {
    // Storage can be unavailable; the lock still covers concurrent tabs.
  }
}

/**
 * Runs `run` in at most one tab per call. A web lock keeps two tabs that both
 * hold the call (a duplicated tab copies this tab's claims) from running it at
 * once, and a localStorage record keeps a later tab from running it after the
 * lock is released. Without the Web Locks API the call just runs.
 */
async function runOnceAcrossTabs(
  toolCallId: string,
  run: () => Promise<boolean>,
  queue: boolean,
  turn?: AbortSignal,
): Promise<RunOutcome> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) {
    if (turn?.aborted) return 'cancelled';
    await run();
    return 'handled';
  }
  const hold = async (lock: Lock | null): Promise<RunOutcome> => {
    if (!lock) return 'contended';
    if (toolCallId in readCompleted(Date.now())) return 'handled';
    // The turn ended while this call waited: leave it unrun.
    if (turn?.aborted) return 'cancelled';
    // Only a delivered result counts as completed; an undelivered one
    // stays unmarked so a later reattach can submit again.
    if (await run()) recordCompleted(toolCallId, Date.now());
    return 'handled';
  };
  const name = `nami-tool:${toolCallId}`;
  if (!queue) return locks.request(name, { ifAvailable: true }, hold);
  return requestQueued(locks, name, hold, turn);
}

/**
 * Waits for the lock, giving up after `LOCK_WAIT_MS` or once `turn` aborts.
 * Resolves `cancelled` when the turn ended, `contended` when the wait timed out.
 */
async function requestQueued(
  locks: LockManager,
  name: string,
  hold: (lock: Lock | null) => Promise<RunOutcome>,
  turn: AbortSignal | undefined,
): Promise<RunOutcome> {
  const waiting = new AbortController();
  const endWait = () => waiting.abort();
  const timer = setTimeout(endWait, LOCK_WAIT_MS);
  turn?.addEventListener('abort', endWait);
  if (turn?.aborted) endWait();
  try {
    return await locks.request(
      name,
      { signal: waiting.signal },
      async (lock) => {
        // Granted: the wait is over, so a long run is not aborted by the timer.
        clearTimeout(timer);
        return hold(lock);
      },
    );
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return turn?.aborted ? 'cancelled' : 'contended';
    }
    throw error;
  } finally {
    clearTimeout(timer);
    turn?.removeEventListener('abort', endWait);
  }
}
